import { Column } from "@ant-design/charts";
import { Button, Card, Col, DatePicker, Empty, message, Row, Select, Space, Spin, Typography } from "antd";
import { GET_SEWING_LINES, SEWING_LINE_DEFECT_MONTHLY_REPORT } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import Header from "components/Common/Header";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getPresetRanges } from "common/SearchForm/DateRangePresets";
import "./index.css";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const RANGE_COUNT = 2;
const CATEGORY_COLORS = ["#4A90D9", "#5BC0BE", "#F5C518", "#8E9AAF", "#7CB342"];

const getDefaultRange = () => [moment().startOf("month"), moment().endOf("month")];

const createEmptyRanges = () => Array.from({ length: RANGE_COUNT }, () => null);

const formatDefectRate = (value) => {
  const rate = Number(value);
  return Number.isFinite(rate) ? `${rate.toFixed(2)}%` : "0.00%";
};

const formatPercentage = (value) => {
  const rate = Number(value);
  return Number.isFinite(rate) ? `${rate.toFixed(2)}%` : "0.00%";
};

const formatCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? count.toLocaleString() : "0";
};

const getLineSortValue = (lineName) => {
  const match = String(lineName || "").match(/Line\s*-?\s*(\d+)/i);

  if (!match) {
    return Number.MAX_SAFE_INTEGER;
  }

  return parseInt(match[1], 10);
};

const sortSewingLinesByNumber = (lines) => {
  return [...(lines || [])].sort((first, second) => {
    const lineNumberCompare = getLineSortValue(first?.name) - getLineSortValue(second?.name);

    if (lineNumberCompare !== 0) {
      return lineNumberCompare;
    }

    return (first?.id || 0) - (second?.id || 0);
  });
};

const getCategoryChartYMax = (maxValue) => {
  if (maxValue <= 0) {
    return 100;
  }

  return Math.min(100, Math.ceil((maxValue + 5) / 10) * 10);
};

const getMonthColSpans = (count) => {
  if (count <= 0) {
    return [24];
  }

  const spans = [];
  let remaining = count;
  const maxColumnsPerRow = count <= 4 ? count : 6;

  while (remaining > 0) {
    const columnsInRow = Math.min(remaining, maxColumnsPerRow);
    const baseSpan = Math.floor(24 / columnsInRow);
    let remainder = 24 - baseSpan * columnsInRow;

    for (let index = 0; index < columnsInRow; index += 1) {
      spans.push(baseSpan + (remainder > 0 ? 1 : 0));
      if (remainder > 0) {
        remainder -= 1;
      }
    }

    remaining -= columnsInRow;
  }

  return spans;
};

const buildCategoryChartConfig = (categories, chartHeight = 220) => {
  const chartData = (categories || []).map((category, index) => {
    const percentage = parseFloat(category.percentage);
    const value = Number.isFinite(percentage) ? percentage : 0;

    return {
      category: category.category_name || `Category ${index + 1}`,
      value,
      label: formatPercentage(value),
    };
  });

  const maxValue = Math.max(...chartData.map((item) => item.value), 0);
  const yMax = getCategoryChartYMax(maxValue);

  return {
    data: chartData,
    xField: "category",
    yField: "value",
    height: chartHeight,
    autoFit: true,
    insetTop: 24,
    insetLeft: 4,
    insetRight: 4,
    legend: false,
    scale: {
      y: {
        domainMin: 0,
        domainMax: yMax,
      },
    },
    style: {
      fill: (datum) => {
        const index = chartData.findIndex((item) => item.category === datum.category);
        return CATEGORY_COLORS[index] ?? CATEGORY_COLORS[0];
      },
      stroke: (datum) => {
        const index = chartData.findIndex((item) => item.category === datum.category);
        return CATEGORY_COLORS[index] ?? CATEGORY_COLORS[0];
      },
      lineWidth: 1,
    },
    axis: {
      x: {
        title: false,
        labelFill: "#1a2b49",
        labelFontWeight: 600,
        labelFontSize: 10,
        labelAutoRotate: true,
      },
      y: {
        title: false,
        labelFontSize: 9,
        labelFormatter: (value) => formatPercentage(value),
        grid: true,
        gridStroke: "#e8e8e8",
        gridLineWidth: 1,
      },
    },
    label: {
      text: (datum) => datum.label,
      position: "top",
      style: {
        fill: "#1a2b49",
        fontSize: 10,
        fontWeight: 600,
        dy: -6,
        textAlign: "center",
        textBaseline: "bottom",
      },
    },
    tooltip: {
      title: "category",
      items: [
        {
          channel: "y",
          name: "Percentage",
          valueFormatter: (value) => formatPercentage(value),
        },
      ],
    },
  };
};

const RANGE_BAR_COLORS = [
  { fill: "l(270) 0:#d8f2fa 0.5:#a8e0f0 1:#7ec8e8", stroke: "#3d9cb5" },
  { fill: "l(270) 0:#e8f5e9 0.5:#c8e6c9 1:#a5d6a7", stroke: "#43a047" },
];

const buildMonthlyChartLabel = (item, allItems) => {
  const duplicateMonthLabels = allItems.filter(
    (chartItem) => chartItem.month_display === item.month_display,
  ).length;

  if (duplicateMonthLabels > 1) {
    return `R${item.range_index} · ${item.month_display}`;
  }

  return item.month_display || item.date_display;
};

const getComparisonChartHeight = (barCount) => Math.max(300, Math.min(520, 260 + barCount * 36));

const getDhuChartYMax = (maxValue) => {
  if (maxValue <= 0) {
    return 3.5;
  }

  return Math.max(3.5, Math.ceil((maxValue + 0.3) * 2) / 2);
};

const normalizeRangeToMonth = (range) => {
  if (!range || !range[0]) {
    return null;
  }

  if (range[1]) {
    return range;
  }

  return [range[0].clone().startOf("day"), range[0].clone().endOf("month")];
};

const isPartialRange = (range) => Boolean(range?.[0] && !range?.[1]);

const finalizePartialRangeAtIndex = (ranges, index, pendingPartialStarts) => {
  const range = ranges[index];
  const pendingStart = pendingPartialStarts[index];

  if (isPartialRange(range)) {
    const next = [...ranges];
    next[index] = normalizeRangeToMonth(range);
    pendingPartialStarts[index] = null;
    return next;
  }

  if ((!range || !range[0]) && pendingStart) {
    const next = [...ranges];
    next[index] = normalizeRangeToMonth([pendingStart, null]);
    pendingPartialStarts[index] = null;
    return next;
  }

  return ranges;
};

const finalizeOtherPartialRanges = (ranges, activeIndex, pendingPartialStarts) => {
  let next = [...ranges];
  let changed = false;

  for (let rangeIndex = 0; rangeIndex < RANGE_COUNT; rangeIndex += 1) {
    if (rangeIndex === activeIndex) {
      continue;
    }

    const updated = finalizePartialRangeAtIndex(next, rangeIndex, pendingPartialStarts);
    if (updated !== next) {
      next = updated;
      changed = true;
    }
  }

  return changed ? next : ranges;
};

const isCompleteRange = (range) => Boolean(range?.[0] && range?.[1]);

const buildDateRangeParams = (ranges) => {
  return ranges
    .map((range) => normalizeRangeToMonth(range))
    .filter(isCompleteRange)
    .map((range) => ({
      from_date: range[0].format("YYYY-MM-DD"),
      to_date: range[1].format("YYYY-MM-DD"),
    }));
};

export default function SewingLineDefectMonthly() {
  const [dateRanges, setDateRanges] = useState(() => {
    const ranges = createEmptyRanges();
    ranges[0] = getDefaultRange();
    return ranges;
  });
  const [lineId, setLineId] = useState(undefined);
  const [sewingLines, setSewingLines] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const pendingPartialStarts = useRef(createEmptyRanges());
  const pickerOpenRef = useRef(Array.from({ length: RANGE_COUNT }, () => false));
  // ux2(final-2): recompute "today" when a picker opens, not just at render (whole-branch
  // review M-2) — a tab left open across midnight otherwise offers a stale "Today"/"Yesterday".
  const [presetRanges, setPresetRanges] = useState(getPresetRanges);

  const fetchSewingLines = useCallback(async () => {
    const response = await getData(GET_SEWING_LINES);

    if (response?.data?.code === 200) {
      setSewingLines(sortSewingLinesByNumber(response?.data?.data || []));
    }
  }, []);

  const fetchReport = useCallback(async (selectedRanges, selectedLineId) => {
    const dateRangeParams = buildDateRangeParams(selectedRanges);

    if (dateRangeParams.length === 0) {
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const params = {
        date_ranges: dateRangeParams,
      };

      if (selectedLineId) {
        params.line_id = selectedLineId;
      }

      const response = await getData(SEWING_LINE_DEFECT_MONTHLY_REPORT, false, params);

      if (response?.data?.code === 200) {
        setReportData(response?.data?.data || null);
      } else {
        setReportData(null);
      }
    } catch (error) {
      console.error(error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSewingLines();
  }, [fetchSewingLines]);

  useEffect(() => {
    const defaultRanges = createEmptyRanges();
    defaultRanges[0] = getDefaultRange();
    fetchReport(defaultRanges, undefined);
  }, [fetchReport]);

  const searchedLineName = useMemo(() => {
    const searchedLineId = reportData?.filters?.line_id;

    if (!searchedLineId) {
      return "All Lines";
    }

    return sewingLines.find((line) => line.id === searchedLineId)?.name || `Line ${searchedLineId}`;
  }, [reportData, sewingLines]);

  const comparisonChartData = useMemo(() => {
    const barChartItems = [...(reportData?.bar_chart || [])].sort((first, second) => {
      const dateCompare = String(first.from_date).localeCompare(String(second.from_date));

      if (dateCompare !== 0) {
        return dateCompare;
      }

      return (first.range_index || 0) - (second.range_index || 0);
    });

    return barChartItems.map((item) => {
      const rate = parseFloat(item.defect_rate);
      const value = Number.isFinite(rate) ? rate : 0;

      return {
        month: buildMonthlyChartLabel(item, barChartItems),
        period: item.date_display,
        rangeIndex: item.range_index,
        sortKey: item.from_date,
        totalDefect: item.total_defect,
        totalGoodGarments: item.total_good_garments,
        value,
        label: formatDefectRate(value),
      };
    });
  }, [reportData]);

  const selectedRangeSummary = useMemo(() => {
    const ranges = reportData?.filters?.date_ranges || [];

    if (ranges.length === 0) {
      return null;
    }

    return ranges
      .map((range, index) => {
        const from = moment(range.from_date).format("DD MMM YYYY");
        const to = moment(range.to_date).format("DD MMM YYYY");
        return `Range ${index + 1}: ${from} - ${to}`;
      })
      .join(" | ");
  }, [reportData]);

  const hasMultipleRanges = (reportData?.filters?.date_ranges || []).length > 1;

  const monthWiseSections = useMemo(() => {
    return reportData?.month_wise_defect_categories || [];
  }, [reportData]);

  const monthColSpans = useMemo(() => getMonthColSpans(monthWiseSections.length), [monthWiseSections.length]);

  const comparisonChartConfig = useMemo(() => {
    const maxValue = Math.max(...comparisonChartData.map((item) => item.value), 0);
    const yMax = getDhuChartYMax(maxValue);
    const chartHeight = getComparisonChartHeight(comparisonChartData.length);

    return {
      data: comparisonChartData,
      xField: "month",
      yField: "value",
      height: chartHeight,
      autoFit: true,
      insetTop: 28,
      insetLeft: 8,
      insetRight: 8,
      legend: false,
      scale: {
        x: {
          domain: comparisonChartData.map((item) => item.month),
        },
        y: {
          domainMin: 0,
          domainMax: yMax,
          tickInterval: 0.5,
        },
      },
      style: {
        fill: (datum) => {
          const colorIndex = Math.max(0, Number(datum.rangeIndex) - 1) % RANGE_BAR_COLORS.length;
          return RANGE_BAR_COLORS[colorIndex].fill;
        },
        stroke: (datum) => {
          const colorIndex = Math.max(0, Number(datum.rangeIndex) - 1) % RANGE_BAR_COLORS.length;
          return RANGE_BAR_COLORS[colorIndex].stroke;
        },
        lineWidth: 1.5,
      },
      axis: {
        x: {
          title: false,
          labelFill: "#1a2b49",
          labelFontWeight: 600,
          labelFontSize: 11,
          labelAutoRotate: comparisonChartData.length > 4,
        },
        y: {
          title: "Defect %",
          titleFill: "#4a5b6d",
          titleFontSize: 11,
          labelFontSize: 10,
          labelFormatter: (value) => formatDefectRate(value),
          grid: true,
          gridStroke: "#e8e8e8",
          gridLineWidth: 1,
        },
      },
      label: {
        text: (datum) => datum.label,
        position: "top",
        style: {
          fill: "#1a2b49",
          fontSize: 11,
          fontWeight: 600,
          dy: -6,
          textAlign: "center",
          textBaseline: "bottom",
        },
      },
      tooltip: {
        title: (datum) => datum.month,
        items: [
          {
            field: "period",
            name: "Period",
          },
          {
            field: "totalDefect",
            name: "Total Defect",
          },
          {
            field: "totalGoodGarments",
            name: "Total Good Garments",
          },
          {
            channel: "y",
            name: "Defect Rate",
            valueFormatter: (value) => formatDefectRate(value),
          },
        ],
      },
    };
  }, [comparisonChartData]);

  const renderCategoryItem = (category, index) => (
    <div className="sewing-line-defect-monthly-category" key={category.category_id || index}>
      <div
        className="sewing-line-defect-monthly-category__badge"
        style={{ backgroundColor: CATEGORY_COLORS[index] }}
      >
        {String(index + 1).padStart(2, "0")}
      </div>
      <div className="sewing-line-defect-monthly-category__content">
        <span className="sewing-line-defect-monthly-category__name">{category.category_name}</span>
        <span className="sewing-line-defect-monthly-category__percentage">
          {formatCount(category.total_defect)} defects · {Number(category.percentage).toFixed(2)}% of month
        </span>
      </div>
    </div>
  );

  const renderMonthCategoryPanel = (monthSection, colSpan) => {
    const categories = monthSection.categories || [];
    const colorIndex = Math.max(0, Number(monthSection.range_index) - 1) % RANGE_BAR_COLORS.length;
    const columnKey = `${monthSection.from_date}-${monthSection.to_date}-${monthSection.range_index}`;
    const chartHeight = colSpan >= 12 ? 240 : 200;
    const chartConfig = buildCategoryChartConfig(categories, chartHeight);

    return (
      <Col
        xs={24}
        sm={monthWiseSections.length === 1 ? 24 : 12}
        lg={colSpan}
        key={columnKey}
        className="sewing-line-defect-monthly-report__month-column-wrap"
      >
        <div
          className={`sewing-line-defect-monthly-report__month-column sewing-line-defect-monthly-report__month-column--range-${monthSection.range_index}`}
        >
          <div
            className="sewing-line-defect-monthly-report__month-column-header"
            style={{ borderTopColor: RANGE_BAR_COLORS[colorIndex].stroke }}
          >
            <Text className="sewing-line-defect-monthly-report__month-column-title">
              {monthSection.month_display}
            </Text>
            <Text className="sewing-line-defect-monthly-report__month-column-period">{monthSection.date_display}</Text>
          </div>

          <div className="sewing-line-defect-monthly-report__month-column-stats">
            <div className="sewing-line-defect-monthly-report__month-stat">
              <span className="sewing-line-defect-monthly-report__month-stat-label">Total Defect</span>
              <span className="sewing-line-defect-monthly-report__month-stat-value">
                {formatCount(monthSection.total_defect)}
              </span>
            </div>
            <div className="sewing-line-defect-monthly-report__month-stat">
              <span className="sewing-line-defect-monthly-report__month-stat-label">Good Garments</span>
              <span className="sewing-line-defect-monthly-report__month-stat-value">
                {formatCount(monthSection.total_good_garments)}
              </span>
            </div>
            <div className="sewing-line-defect-monthly-report__month-stat">
              <span className="sewing-line-defect-monthly-report__month-stat-label">Defect Rate</span>
              <span className="sewing-line-defect-monthly-report__month-stat-value sewing-line-defect-monthly-report__month-stat-value--rate">
                {formatDefectRate(monthSection.defect_rate)}
              </span>
            </div>
          </div>

          <div className="sewing-line-defect-monthly-report__month-column-body">
            <Text className="sewing-line-defect-monthly-report__month-column-subtitle">Top 5 Defect Categories</Text>

            <div className="sewing-line-defect-monthly-report__chart sewing-line-defect-monthly-report__chart--panel">
              {categories.length > 0 ? (
                <Column
                  key={`${columnKey}-${categories.map((item) => item.category_id).join("-")}`}
                  {...chartConfig}
                />
              ) : (
                <div className="sewing-line-defect-monthly-empty sewing-line-defect-monthly-empty--compact">
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No category data" />
                </div>
              )}
            </div>

            {categories.length > 0 && (
              <div className="sewing-line-defect-monthly-report__month-column-categories">
                {categories.map((category, index) => renderCategoryItem(category, index))}
              </div>
            )}
          </div>
        </div>
      </Col>
    );
  };

  const updateRange = (index, value) => {
    if (!value) {
      if (pendingPartialStarts.current[index] && !pickerOpenRef.current[index]) {
        return;
      }

      pendingPartialStarts.current[index] = null;
      setDateRanges((prev) => {
        const next = [...prev];
        next[index] = null;
        return next;
      });
      return;
    }

    if (value[0] && !value[1]) {
      pendingPartialStarts.current[index] = value[0].clone();
    } else {
      pendingPartialStarts.current[index] = null;
    }

    setDateRanges((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleCalendarChange = (index, dates) => {
    if (!dates || (!dates[0] && !dates[1])) {
      pendingPartialStarts.current[index] = null;
      return;
    }

    if (dates[0] && !dates[1]) {
      pendingPartialStarts.current[index] = dates[0].clone();
      return;
    }

    pendingPartialStarts.current[index] = null;
  };

  const scheduleFinalizePartialRanges = (activeIndex, includeActiveRange) => {
    window.setTimeout(() => {
      setDateRanges((prev) => {
        let next = finalizeOtherPartialRanges(prev, activeIndex, pendingPartialStarts.current);

        if (includeActiveRange) {
          const updated = finalizePartialRangeAtIndex(next, activeIndex, pendingPartialStarts.current);
          if (updated !== next) {
            next = updated;
          }
        }

        return next === prev ? prev : next;
      });
    }, 0);
  };

  const handleRangeOpenChange = (index, open) => {
    pickerOpenRef.current[index] = open;

    if (open) {
      setPresetRanges(getPresetRanges());
      scheduleFinalizePartialRanges(index, false);
      return;
    }

    scheduleFinalizePartialRanges(index, true);
  };

  const handleSearch = () => {
    const normalizedRanges = dateRanges.map((range) => normalizeRangeToMonth(range));
    const completeRanges = normalizedRanges.filter(isCompleteRange);

    if (completeRanges.length === 0) {
      message.warning("Please select at least one date range");
      return;
    }

    setDateRanges(normalizedRanges);
    fetchReport(normalizedRanges, lineId);
  };

  const handleReset = () => {
    const ranges = createEmptyRanges();
    ranges[0] = getDefaultRange();
    pendingPartialStarts.current = createEmptyRanges();
    pickerOpenRef.current = Array.from({ length: RANGE_COUNT }, () => false);
    setDateRanges(ranges);
    setLineId(undefined);
    fetchReport(ranges, undefined);
  };

  const hasAtLeastOneRange = buildDateRangeParams(dateRanges).length > 0;

  return (
    <div className="sewing-line-defect-monthly-page">
      <Header title="Sewing Line Defect (Monthly)" />

      <Card bordered={false} className="sewing-line-defect-monthly-filters" bodyStyle={{ padding: "12px 16px" }}>
        <Row gutter={[12, 8]} align="bottom">
          {Array.from({ length: RANGE_COUNT }).map((_, index) => (
            <Col xs={24} sm={12} md={8} lg={6} xl={5} key={`date-range-${index + 1}`}>
              <label className="sewing-line-defect-monthly-filters__label">
                Date Range {index + 1}
                {index === 0 ? <span style={{ color: "red" }}> *</span> : null}
              </label>
              <RangePicker
                allowClear
                size="small"
                style={{ width: "100%" }}
                format="DD-MMM-YYYY"
                placeholder={["Start Date", "End Date"]}
                value={dateRanges[index]}
                ranges={presetRanges}
                onChange={(value) => updateRange(index, value)}
                onCalendarChange={(dates) => handleCalendarChange(index, dates)}
                onOpenChange={(open) => handleRangeOpenChange(index, open)}
              />
            </Col>
          ))}
          <Col xs={24} sm={12} md={8} lg={6} xl={4}>
            <label className="sewing-line-defect-monthly-filters__label">Line</label>
            <Select
              allowClear
              showSearch
              size="small"
              placeholder="All Lines"
              optionFilterProp="children"
              style={{ width: "100%" }}
              value={lineId}
              onChange={(value) => setLineId(value)}
            >
              {sewingLines.map((line) => (
                <Select.Option key={line.id} value={line.id}>
                  {line.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6} xl={5}>
            <div className="sewing-line-defect-monthly-filters__actions">
              <Space size={8}>
                <Button
                  type="primary"
                  size="small"
                  onClick={handleSearch}
                  loading={loading}
                  disabled={!hasAtLeastOneRange || loading}
                >
                  Search
                </Button>
                <Button size="small" onClick={handleReset} disabled={loading}>
                  Reset
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      <Spin spinning={loading}>
        <div className="sewing-line-defect-monthly-report">
          {!hasSearched ? (
            <div className="sewing-line-defect-monthly-empty">
              <Empty description="Select at least one date range and click Search to view report" />
            </div>
          ) : (
            <>
              <div className="sewing-line-defect-monthly-report__comparison">
                <div className="sewing-line-defect-monthly-report__header">
                  <Title level={5} className="sewing-line-defect-monthly-report__title">
                    Sewing Line - {searchedLineName}
                  </Title>
                  <Text className="sewing-line-defect-monthly-report__subtitle">Monthly Defect Percentage</Text>
                  {selectedRangeSummary ? (
                    <Text className="sewing-line-defect-monthly-report__range-summary">{selectedRangeSummary}</Text>
                  ) : null}
                  {hasMultipleRanges ? (
                    <div className="sewing-line-defect-monthly-report__legend">
                      <span className="sewing-line-defect-monthly-report__legend-item sewing-line-defect-monthly-report__legend-item--range-1">
                        Date Range 1
                      </span>
                      <span className="sewing-line-defect-monthly-report__legend-item sewing-line-defect-monthly-report__legend-item--range-2">
                        Date Range 2
                      </span>
                    </div>
                  ) : null}
                </div>

                <div
                  className="sewing-line-defect-monthly-report__chart sewing-line-defect-monthly-report__chart--comparison"
                  style={{ minHeight: getComparisonChartHeight(comparisonChartData.length) }}
                >
                  {comparisonChartData.length > 0 ? (
                    <Column
                      key={comparisonChartData.map((item) => `${item.month}-${item.period}-${item.value}`).join("-")}
                      {...comparisonChartConfig}
                    />
                  ) : (
                    <div className="sewing-line-defect-monthly-empty">
                      <Empty description="No comparison data found for the selected filters" />
                    </div>
                  )}
                </div>
              </div>

              <div className="sewing-line-defect-monthly-report__category-section">
                <div className="sewing-line-defect-monthly-report__header sewing-line-defect-monthly-report__header--panel">
                  <Text className="sewing-line-defect-monthly-report__subtitle">Top 5 Defect Categories (Month-wise)</Text>
                  <Text className="sewing-line-defect-monthly-report__category-note">
                    One column per month, aligned with the bars above
                  </Text>
                </div>

                {monthWiseSections.length > 0 ? (
                  <Row gutter={0} className="sewing-line-defect-monthly-report__month-grid">
                    {monthWiseSections.map((monthSection, index) =>
                      renderMonthCategoryPanel(monthSection, monthColSpans[index] || 24),
                    )}
                  </Row>
                ) : (
                  <div className="sewing-line-defect-monthly-empty">
                    <Empty description="No month-wise defect category data found for the selected filters" />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Spin>
    </div>
  );
}
